// Utility functions for managing column visibility state persistence

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  sortable: boolean;
}

// Default column configurations for different pages
export const getDefaultLearningProgressColumns = (): ColumnConfig[] => [
  { key: "USN", label: "USN", visible: true, sortable: true },
  { key: "Name", label: "Name", visible: true, sortable: true },
  { key: "Score", label: "Score", visible: true, sortable: true },
  { key: "Score_%", label: "Score in %", visible: true, sortable: true },
  {
    key: "No_of_hours",
    label: "No of hours",
    visible: true,
    sortable: true,
  },
  {
    key: "Performance_Type",
    label: "Timeliness",
    visible: true,
    sortable: true,
  },
  {
    key: "performance_percentage",
    label: "Progress",
    visible: true,
    sortable: true,
  },
  {
    key: "Delays",
    label: "Delay in days",
    visible: true,
    sortable: true,
  },
  {
    key: "College_Rank",
    label: "College Rank",
    visible: true,
    sortable: true,
  },
  {
    key: "Overall_Rank",
    label: "Overall Rank",
    visible: true,
    sortable: true,
  },
  { key: "Catogory", label: "Category", visible: true, sortable: false },
  { key: "CGPA", label: "CGPA", visible: true, sortable: true },
  { key: "Branch", label: "Branch", visible: true, sortable: true },
  { key: "ID", label: "ID", visible: false, sortable: true },
  { key: "Email", label: "Email", visible: false, sortable: true },
  { key: "Mobile", label: "Phone no.", visible: false, sortable: true },
  { key: "college_batch", label: "College Batch", visible: false, sortable: true },
];

export const getDefaultCoordinatorReportColumns = (): ColumnConfig[] => [
  { key: "USN", label: "USN", visible: true, sortable: true },
  { key: "Name", label: "Name", visible: true, sortable: true },
  { key: "Score", label: "Score", visible: true, sortable: true },
  { key: "Score_%", label: "Score in %", visible: true, sortable: true },
  {
    key: "No_of_hours",
    label: "No of hours",
    visible: true,
    sortable: true,
  },
  {
    key: "Performance_Type",
    label: "Timeliness",
    visible: true,
    sortable: true,
  },
  {
    key: "performance_percentage",
    label: "Progress",
    visible: true,
    sortable: true,
  },
  {
    key: "Delays",
    label: "Delay in days",
    visible: true,
    sortable: true,
  },
  {
    key: "College_Rank",
    label: "College Rank",
    visible: true,
    sortable: true,
  },
  {
    key: "Overall_Rank",
    label: "Overall Rank",
    visible: true,
    sortable: true,
  },
  { key: "Catogory", label: "Category", visible: true, sortable: false },
  { key: "CGPA", label: "CGPA", visible: true, sortable: true },
  { key: "Branch", label: "Branch", visible: true, sortable: true },
  { key: "ID", label: "ID", visible: false, sortable: true },
  { key: "Email", label: "Email", visible: false, sortable: true },
  { key: "Mobile", label: "Phone no.", visible: false, sortable: true },
  { key: "college_batch", label: "College Batch", visible: false, sortable: true },
];

// Save column visibility state to sessionStorage
export const saveColumnVisibility = (pageKey: string, columns: ColumnConfig[]): void => {
  try {
    const columnState = columns.map(col => ({
      key: col.key,
      visible: col.visible
    }));
    sessionStorage.setItem(`columnVisibility_${pageKey}`, JSON.stringify(columnState));
  } catch (error) {
    console.error('Error saving column visibility:', error);
  }
};

// Load column visibility state from sessionStorage
export const loadColumnVisibility = (pageKey: string, defaultColumns: ColumnConfig[]): ColumnConfig[] => {
  try {
    const savedState = sessionStorage.getItem(`columnVisibility_${pageKey}`);
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      
      // Merge saved visibility state with default columns
      return defaultColumns.map(defaultCol => {
        const savedCol = parsedState.find((saved: any) => saved.key === defaultCol.key);
        return {
          ...defaultCol,
          visible: savedCol ? savedCol.visible : defaultCol.visible
        };
      });
    }
  } catch (error) {
    console.error('Error loading column visibility:', error);
  }
  
  return defaultColumns;
};

// Update column visibility and save to sessionStorage
export const updateColumnVisibility = (
  pageKey: string, 
  columns: ColumnConfig[], 
  columnKey: string
): ColumnConfig[] => {
  const updatedColumns = columns.map(col =>
    col.key === columnKey ? { ...col, visible: !col.visible } : col
  );
  
  // Save the updated state
  saveColumnVisibility(pageKey, updatedColumns);
  
  return updatedColumns;
};

// Reset column visibility to default
export const resetColumnVisibility = (pageKey: string, defaultColumns: ColumnConfig[]): ColumnConfig[] => {
  try {
    sessionStorage.removeItem(`columnVisibility_${pageKey}`);
  } catch (error) {
    console.error('Error resetting column visibility:', error);
  }
  
  return defaultColumns;
};

export const getDefaultSubBatchDetailsColumns = (): ColumnConfig[] => [
  { key: "USN", label: "USN", visible: true, sortable: true },
  { key: "Name", label: "Name", visible: true, sortable: true },
  { key: "Batch", label: "Batch", visible: true, sortable: true },
  { key: "Progress", label: "Timeliness", visible: true, sortable: true },
  { key: "performance_percentage", label: "Progress %", visible: true, sortable: true },
  { key: "Time_Spent", label: "No of hours", visible: true, sortable: true },
  { key: "Score_WT", label: "Score WT", visible: true, sortable: true },
  { key: "Live_Sessions", label: "Live Sessions", visible: true, sortable: true },
  { key: "Score_FT", label: "Score FT", visible: true, sortable: true },
  { key: "Grade", label: "Grade", visible: true, sortable: true },
];

export const getDefaultProjectColumns = (): ColumnConfig[] => [
  { key: "SL_No", label: "SL No", visible: true, sortable: false },
  { key: "ID", label: "ID", visible: false, sortable: true },
  { key: "project_name", label: "Project Name", visible: true, sortable: true },
  { key: "project_description", label: "Project Desc", visible: true, sortable: true },
  { key: "company_name", label: "Company", visible: true, sortable: true },
  { key: "industry", label: "Domain", visible: true, sortable: true },
  { key: "status", label: "Status", visible: true, sortable: false },
  { key: "batch", label: "Batch", visible: true, sortable: false },
  { key: "actions", label: "Action", visible: true, sortable: false },
];

// Page keys for different components
export const PAGE_KEYS = {
  LEARNING_PROGRESS: 'learningProgress',
  COORDINATOR_REPORT: 'coordinatorReport',
  SUB_BATCH_DETAILS: 'subBatchDetails',
  PROJECT: 'project',
} as const;
